import React, { useCallback, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { showToast } from 'components/ui/utils';
import { ConfirmModal } from 'components/ui/ConfirmModal/ConfirmModal';
import { HubOption, HUB_MAX_REGULAR_OPTIONS, isContinueOption } from 'dorian-shared/types/branch/HubOption';
import { useClipboardState } from '../hooks/useClipboardState';
import { useHubSelection } from '../hooks/useHubSelection';
import { BranchSelectItem } from '../shared/types';
import { useScrollToNewOption } from '../shared/useScrollToNewOption';
import { copyOptions, pasteOptions } from './hubClipboard';
import { HubClipboardToolbar } from './HubClipboardToolbar';
import { HubOptionCard } from './HubOptionCard';

type HubOptionsListProps = {
  options: HubOption[];
  branches: BranchSelectItem[];
  currentBranchId: number | null;
  isDisabled: boolean;
  onAdd: () => void;
  onDelete: (uuids: string[]) => void;
  onUpdate: (uuid: string, changes: Partial<HubOption>) => void;
  onDuplicate: (uuid: string) => void;
  onPaste: (pastedOptions: HubOption[]) => { inserted: number; skipped: number };
};

function sortOptions(options: HubOption[]): HubOption[] {
  return [...options].sort((a, b) => {
    if (isContinueOption(a) && !isContinueOption(b)) return 1;
    if (!isContinueOption(a) && isContinueOption(b)) return -1;
    return a.sortOrder - b.sortOrder;
  });
}

export function HubOptionsList({
  options,
  branches,
  currentBranchId,
  isDisabled,
  onAdd,
  onDelete,
  onUpdate,
  onDuplicate,
  onPaste,
}: HubOptionsListProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const clipboardHasContent = useClipboardState();
  const {
    selectedUuids,
    selectedCount,
    allSelected,
    handleToggleSelect,
    handleSelectAll,
    handleDeselectAll,
  } = useHubSelection(options);

  const sorted = useMemo(() => sortOptions(options), [options]);
  const regularCount = options.filter((o) => !isContinueOption(o)).length;
  const isMaxReached = regularCount >= HUB_MAX_REGULAR_OPTIONS;
  const isAddDisabled = isDisabled || isMaxReached;

  const { listRef, newOptionUuid, trackBeforeAdd } = useScrollToNewOption(options);

  const handleAdd = useCallback(() => {
    trackBeforeAdd();
    onAdd();
  }, [onAdd, trackBeforeAdd]);

  const handleDuplicate = useCallback((uuid: string) => {
    trackBeforeAdd();
    onDuplicate(uuid);
  }, [onDuplicate, trackBeforeAdd]);

  const handleCopy = useCallback(() => {
    const selected = options.filter((o) => selectedUuids.has(o.uuid));
    copyOptions(selected);
  }, [options, selectedUuids]);

  const selectedOptionTitles = useMemo(
    () => options
      .filter((o) => selectedUuids.has(o.uuid))
      .map((o) => ({ uuid: o.uuid, title: o.title || '(untitled)' })),
    [options, selectedUuids],
  );

  const handleBulkDelete = useCallback(() => {
    onDelete(Array.from(selectedUuids));
    handleDeselectAll();
    setShowDeleteConfirm(false);
  }, [onDelete, selectedUuids, handleDeselectAll]);

  const handlePaste = useCallback(() => {
    const pasted = pasteOptions();
    if (pasted.length === 0) return;

    trackBeforeAdd();
    const { inserted, skipped } = onPaste(pasted);

    if (skipped > 0) {
      showToast({
        textMessage: `Pasted ${inserted} option${inserted !== 1 ? 's' : ''}. ${skipped} option${skipped !== 1 ? 's' : ''} skipped (max ${HUB_MAX_REGULAR_OPTIONS} reached).`,
      });
    }
  }, [onPaste, trackBeforeAdd]);

  return (
    <div ref={listRef}>
      <HubClipboardToolbar
        selectedCount={selectedCount}
        regularCount={regularCount}
        allSelected={allSelected}
        isDisabled={isDisabled}
        isMaxReached={isMaxReached}
        clipboardHasContent={clipboardHasContent}
        onSelectAll={handleSelectAll}
        onDeselectAll={handleDeselectAll}
        onCopy={handleCopy}
        onPaste={handlePaste}
        onDelete={() => setShowDeleteConfirm(true)}
      />

      {sorted.map((option, index) => (
        <HubOptionCard
          key={option.uuid}
          option={option}
          index={index}
          branches={branches}
          currentBranchId={currentBranchId}
          isDisabled={isDisabled}
          isMaxReached={isMaxReached}
          isSelected={selectedUuids.has(option.uuid)}
          onToggleSelect={handleToggleSelect}
          defaultExpanded={option.uuid === newOptionUuid}
          onUpdate={onUpdate}
          onDelete={onDelete}
          onDuplicate={handleDuplicate}
        />
      ))}

      <div className="mt-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={isAddDisabled}
          onClick={handleAdd}
        >
          + Add Hub Option
        </Button>
        <small className="text-muted ml-2">
          {regularCount} / {HUB_MAX_REGULAR_OPTIONS} options (+ Continue)
        </small>
      </div>

      <ConfirmModal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        title="Delete Hub Options"
        confirmButtonTitle="Delete"
        isLoading={false}
        onConfirm={handleBulkDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      >
        <p>Are you sure you want to delete {selectedOptionTitles.length} hub option{selectedOptionTitles.length !== 1 ? 's' : ''}?</p>
        <ul>
          {selectedOptionTitles.map((o) => (
            <li key={o.uuid}>{o.title}</li>
          ))}
        </ul>
      </ConfirmModal>
    </div>
  );
}
